// client/src/context/ToastContext.js
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ToastViewport from "../components/Toast/ToastViewport";

export const ToastContext = createContext(null);

const DEFAULT_DURATION = 4000;
const EXIT_MS = 200;

// Centered is the default placement. A rapid barcode scan should never build a
// tower over the kiosk input, so it still caps well below the corner.
const MAX_TOASTS = { "top-end": 4, center: 3 };

// Copy used on more than one page. Kept here so the wording stays identical
// everywhere and there is a single place to change it.
export const COPY = {
  unreachable: "We couldn't reach the server. Check your connection and try again.",
  serverError: "Something went wrong on our end. Please try again in a moment.",
  rateLimited: "Too many attempts. Please wait a moment and try again.",
  sessionExpired: "Your session expired. Please sign in again.",
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Monotonic counter, not Date.now(): two submits inside the same millisecond
  // would collide on their React key.
  const idRef = useRef(0);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    const dismissTimer = timersRef.current.get(id);
    if (dismissTimer) {
      clearTimeout(dismissTimer);
      timersRef.current.delete(id);
    }

    setToasts((current) =>
      current.map((t) => (t.id === id ? { ...t, leaving: true } : t))
    );

    const exitTimer = setTimeout(() => {
      setToasts((current) => current.filter((t) => t.id !== id));
      timersRef.current.delete(`exit-${id}`);
    }, EXIT_MS);
    timersRef.current.set(`exit-${id}`, exitTimer);
  }, []);

  const show = useCallback(
    (variant, content, options = {}) => {
      const {
        duration = DEFAULT_DURATION,
        size = "default",
        placement = "center",
        dedupeKey,
      } = options;

      const id = (idRef.current += 1);

      setToasts((current) => {
        let next = current;

        // A repeat of the same dedupeKey replaces its predecessor instead of stacking.
        if (dedupeKey) {
          next = next.filter((t) => t.dedupeKey !== dedupeKey);
        }

        const samePlacement = next.filter((t) => t.placement === placement);
        const cap = MAX_TOASTS[placement] ?? 4;
        if (samePlacement.length >= cap) {
          const evicted = new Set(
            samePlacement.slice(0, samePlacement.length - cap + 1).map((t) => t.id)
          );
          next = next.filter((t) => !evicted.has(t.id));
        }

        return [
          ...next,
          { id, variant, content, size, placement, dedupeKey, leaving: false },
        ];
      });

      if (duration > 0) {
        timersRef.current.set(id, setTimeout(() => dismiss(id), duration));
      }
      return id;
    },
    [dismiss]
  );

  // Reconcile timers against what is actually rendered. This is what cleans up
  // after a dedupe replacement or a stack-cap eviction, and it keeps the state
  // updater in `show` free of side effects.
  useEffect(() => {
    const live = new Set(toasts.map((t) => t.id));
    timersRef.current.forEach((handle, key) => {
      const id = typeof key === "string" ? Number(key.slice("exit-".length)) : key;
      if (!live.has(id)) {
        clearTimeout(handle);
        timersRef.current.delete(key);
      }
    });
  }, [toasts]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  const toast = useMemo(
    () => ({
      success: (content, options) => show("success", content, options),
      error: (content, options) => show("error", content, options),
      warning: (content, options) => show("warning", content, options),
      info: (content, options) => show("info", content, options),
      show,
      dismiss,
    }),
    [show, dismiss]
  );

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used inside a ToastProvider");
  }
  return context;
};
